import React, { useEffect } from 'react';
import { ConsentAwareAnalyticsProvider } from '@/providers/ConsentAwareAnalyticsProvider';
import {
  CookieConsentProvider,
  useCookieConsent,
} from '@/providers/CookieConsentProvider';
import { CookieBanner } from '@/components/CookieBanner';
import { CookiePreferencesDialog } from '@/components/CookiePreferencesDialog';
import { useTheme } from './hooks/useTheme';

interface AppWithCookieConsentProps {
  children: React.ReactNode;
}

/**
 * Inner component that has access to cookie consent context
 */
function AppContent({ children }: { children: React.ReactNode }) {
  const { preferences } = useCookieConsent();
  const { theme } = useTheme();

  useEffect(() => {
    const favicon = document.getElementById('favicon') as HTMLLinkElement;
    if (favicon) {
      favicon.href =
        theme === 'dark' ? '/favicon-dark.svg' : '/favicon-light.svg';
    } else {
      console.error('Favicon not found');
    }
  }, [theme]);

  return (
    <ConsentAwareAnalyticsProvider cookiePreferences={preferences}>
      {children}
      <CookieBanner />
      <CookiePreferencesDialog />
    </ConsentAwareAnalyticsProvider>
  );
}

/**
 * Wrapper component that provides cookie consent functionality
 *
 * Usage in main.tsx:
 *
 * Replace:
 * <AnalyticsProvider>
 *   <Outlet />
 * </AnalyticsProvider>
 *
 * With:
 * <AppWithCookieConsent>
 *   <Outlet />
 * </AppWithCookieConsent>
 */
export function AppWithCookieConsent({ children }: AppWithCookieConsentProps) {
  return (
    <CookieConsentProvider>
      <AppContent>{children}</AppContent>
    </CookieConsentProvider>
  );
}
